import { useState, Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { createSupportTicket } from '@/lib/support/tickets';

export default function SupportBubble() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (close: () => void) => {
    if (!subject.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      const ticket = await createSupportTicket({
        subject: subject.trim(),
        category: 'QUESTION',
        priority: 'NORMAL',
        body: body.trim(),
      });
      toast.success(t('support.ticketCreated'));
      setSubject('');
      setBody('');
      close();
      navigate(`/support/tickets/${ticket.id}`);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover className="fixed bottom-5 right-5 z-40">
      {({ open, close }) => (
        <>
          <Popover.Button
            className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-700 text-white shadow-xl hover:bg-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t('support.bubble.openHelp')}
          >
            {open ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <ChatBubbleLeftRightIcon className="h-6 w-6" />
            )}
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <Popover.Panel className="absolute bottom-16 right-0 w-80 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl">
              <h3 className="text-sm font-semibold text-gray-900">{t('support.bubble.title')}</h3>
              <p className="mt-1 text-xs text-gray-500">{t('support.bubble.subtitle')}</p>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('support.subject')}
                className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                placeholder={t('support.bubble.placeholder')}
                className="mt-2 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="text-xs font-medium text-primary-700 hover:underline"
                >
                  {t('support.viewAll')}
                </button>
                <button
                  type="button"
                  onClick={() => submit(close)}
                  disabled={submitting || !subject.trim() || !body.trim()}
                  className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('support.send')}
                </button>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
